import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { ThemeInit } from '../.flowbite-react/init';
import { MainLayout } from './layouts/MainLayout';
import { AuthProvider } from './contexts/AuthContext';
import { AppSettingsProvider } from './contexts/AppSettingsContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { PostLoginRedirect } from './components/auth/PostLoginRedirect';
import { auth0Config, isAuth0Configured } from './auth0Config';
import { rememberPostLoginPath } from './utils/postLoginRedirect';
import {
  HomePage,
  OrganizationsPage,
  OrganizationProfilePage,
  PersonProfilePage,
  SearchContactsPage,
  InteractiveMapPage,
  StaffManagementPage,
  UserManagementPage,
  RoleManagementPage,
  DescriptorManagementPage,
  ActivityMonitorPage,
  DataHealthPage,
  OrganizationLogosPage,
  AccessDeniedPage,
  LoginPage,
  SSOLandingPage,
  CalendarPage,
  MyCalendarFeedsPage
} from './pages';
import { HelpPage } from './pages/HelpPage';
import { AppSettingsPage } from './pages/AppSettingsPage';

// Auth0 callback handler for redirects. Auth0 hands control back before React
// Router notices URL changes, so the return path is remembered for
// PostLoginRedirect to navigate to, and the URL is cleared of Auth0's
// code/state params.
const onRedirectCallback = (appState: { returnTo?: string } | undefined) => {
  rememberPostLoginPath(appState?.returnTo);
  window.history.replaceState({}, document.title, window.location.pathname);
};

function AppContent() {
  return (
    <Router>
      <PostLoginRedirect />
      <Routes>
        {/* Public routes - no authentication required */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/access-denied" element={<AccessDeniedPage />} />
        <Route path="/sso" element={<SSOLandingPage />} />

        {/* Protected application routes - require authentication */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="leas" element={<OrganizationsPage />} />
          <Route path="leas/:id" element={<OrganizationProfilePage />} />
          <Route path="persons/:personId" element={<PersonProfilePage />} />
          <Route path="search" element={<SearchContactsPage />} />
          <Route path="map" element={<InteractiveMapPage />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="me/calendar-feeds" element={<MyCalendarFeedsPage />} />

          {/* Admin Routes - require superAdmin role */}
          <Route path="admin">
            <Route path="staff" element={<StaffManagementPage />} />
            <Route path="users" element={<UserManagementPage />} />
            <Route path="roles" element={<RoleManagementPage />} />
            <Route path="descriptors" element={<DescriptorManagementPage />} />
            <Route path="activity" element={<ActivityMonitorPage />} />
            <Route path="data-health" element={<DataHealthPage />} />
            <Route path="logos" element={<OrganizationLogosPage />} />
            <Route path="app-settings" element={<AppSettingsPage />} />
          </Route>
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  // Check if Auth0 is configured
  if (!isAuth0Configured()) {
    console.warn('Auth0 is not configured. Running in development mode with mock authentication.');
    // In development without Auth0, still render the app
    // The AuthProvider will handle mock authentication
    return (
      <>
        <ThemeInit />
        <AuthProvider>
          <AppSettingsProvider>
            <AppContent />
          </AppSettingsProvider>
        </AuthProvider>
      </>
    );
  }

  return (
    <>
      <ThemeInit />
      <Auth0Provider
        domain={auth0Config.domain}
        clientId={auth0Config.clientId}
        authorizationParams={auth0Config.authorizationParams}
        cacheLocation={auth0Config.cacheLocation}
        useRefreshTokens={auth0Config.useRefreshTokens}
        onRedirectCallback={onRedirectCallback}
      >
        <AuthProvider>
          <AppSettingsProvider>
            <AppContent />
          </AppSettingsProvider>
        </AuthProvider>
      </Auth0Provider>
    </>
  );
}

export default App;
